import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin, Youtube, Twitter, ImageIcon } from 'lucide-react';

const Footer = () => {
  const { settings, categories } = useApp();
  const location = useLocation();

  if (location.pathname.startsWith('/admin')) return null;

  // Build 2x3 QR grid from settings (up to 6 images)
  const galleryImages = Array.isArray(settings.footer_gallery)
    ? settings.footer_gallery
    : [];
  const cells = Array.from({ length: 6 }, (_, i) => galleryImages[i] || null);

  // Social media links mapped to each QR slot (1-Facebook, 2-Instagram, 3-LinkedIn, 4-X, 5-YouTube, 6-TikTok)
  const socialLinks = [
    { label: 'Facebook',  url: 'https://www.facebook.com/profile.php?id=61587535891263' },
    { label: 'Instagram', url: 'https://www.instagram.com/grandpalace.ae?igsh=MWdtaThhcTd6dG5oeA%3D%3D&utm_source=qr' },
    { label: 'LinkedIn',  url: 'https://www.linkedin.com/in/grand-palace-general-trading-llc-2904063a8?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=ios_app' },
    { label: 'X',        url: 'https://x.com/GrandPalace2022' },
    { label: 'YouTube',  url: 'https://www.youtube.com/channel/UCINXOBFtfMPvDSsXRMJM48w' },
    { label: 'TikTok',   url: 'https://www.tiktok.com/@grandpalace.ae?is_from_webapp=1&sender_device=pc' },
  ];

  return (
    <footer className="bg-gray-50 text-gray-700 border-t border-[#0c1b44]">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">

          {/* Column 1 – Logo + tagline + contact info */}
          <div className="flex flex-col items-center md:items-start text-center md:text-left">
            <div className="mb-4">
              <img
                src={settings.header_logo ? (settings.header_logo.startsWith('/api') ? `${window.location.origin}${settings.header_logo}` : settings.header_logo) : `${process.env.PUBLIC_URL}/logo.png`}
                alt="Grand Palace Logo"
                className="h-10 w-auto object-contain mix-blend-multiply mx-auto md:mx-0"
                onError={(e) => { e.target.onerror = null; e.target.src = `${process.env.PUBLIC_URL}/logo.png`; }}
              />
            </div>
            <p className="text-gray-400 mb-5 text-sm">Your trusted partner in construction materials and general trading across UAE.</p>

            {/* Contact information moved here */}
            <div className="space-y-3">
              <p className="flex items-start gap-2 text-gray-400 text-sm">
                <MapPin className="h-4 w-4 text-[#0c1b44] shrink-0 mt-0.5" />
                <span>{settings.company_address || 'Shop No:3 Al Sajaya Building, Al Qusais 2, Damascus St. 244, Sector, Dubai, United Arab Emirates'}</span>
              </p>
              <a href={`tel:${settings.company_mobile || '+971503992163'}`} className="flex items-center gap-2 text-gray-400 hover:text-[#0c1b44] text-sm">
                <Phone className="h-4 w-4 text-[#0c1b44] shrink-0" />
                {settings.company_mobile || '+971 50 399 2163'}
              </a>
              <a href={`tel:${settings.company_phone || '+97142727815'}`} className="flex items-center gap-2 text-gray-400 hover:text-[#0c1b44] text-sm">
                <Phone className="h-4 w-4 text-[#0c1b44] shrink-0" />
                {settings.company_phone || '+971 4 272 7815'}
              </a>
              <a href={`mailto:${settings.company_email || 'sales@gpgt.ae'}`} className="flex items-center gap-2 text-gray-400 hover:text-[#0c1b44] text-sm">
                <Mail className="h-4 w-4 text-[#0c1b44] shrink-0" />
                {settings.company_email || 'sales@gpgt.ae'}
              </a>
            </div>
          </div>

          {/* Column 2 – Categories */}
          <div className="text-center md:text-left">
            <h4 className="text-lg font-semibold mb-4">Categories</h4>
            <ul className="space-y-2">
              {categories.slice(0, 6).map(cat => (
                <li key={cat.id}>
                  <Link to={`/products?category=${cat.id}`} className="text-gray-400 hover:text-[#0c1b44] transition-colors">{cat.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 – Quick Links */}
          <div className="text-center md:text-left">
            <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li><Link to="/products" className="text-gray-400 hover:text-[#0c1b44] transition-colors">Products</Link></li>
              <li><Link to="/catalog" className="text-gray-400 hover:text-[#0c1b44] transition-colors">Catalog</Link></li>
              <li><Link to="/cart" className="text-gray-400 hover:text-[#0c1b44] transition-colors">Shopping Cart</Link></li>
              <li><Link to="/contact" className="text-gray-400 hover:text-[#0c1b44] transition-colors">Contact Us</Link></li>
              <li><Link to="/login" className="text-gray-400 hover:text-[#0c1b44] transition-colors">My Account</Link></li>
            </ul>
          </div>

          {/* Column 4 – 2×3 QR code grid */}
          <div>
            <h4 className="text-lg font-semibold mb-1">Scan to Connect</h4>
            <p className="text-xs text-gray-400 mb-3 leading-snug">Click or Scan our QR codes<br />to follow us on social media</p>
            <div className="grid grid-cols-3 gap-1.5">
              {cells.map((src, i) => {
                const social = socialLinks[i];
                return src ? (
                  <a
                    key={i}
                    href={social ? social.url : '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={social ? `Connect with us` : `QR Code ${i + 1}`}
                    className="flex flex-col items-center gap-0.5 cursor-pointer group"
                  >
                    <div className="aspect-square w-full rounded overflow-hidden bg-gray-200 ring-2 ring-transparent group-hover:ring-[#0c1b44] transition-all duration-200">
                      <img
                        src={src.startsWith('/api') ? `${window.location.origin}${src}` : src}
                        alt={`QR Code ${i + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {social && (
                      <span className="text-[9px] text-gray-400 group-hover:text-[#0c1b44] transition-colors leading-none">{social.label}</span>
                    )}
                  </a>
                ) : (
                  <div
                    key={i}
                    className="flex flex-col items-center gap-0.5"
                  >
                    <div className="aspect-square w-full rounded bg-gray-100 border border-dashed border-gray-300 flex items-center justify-center">
                      <ImageIcon className="h-4 w-4 text-gray-300" />
                    </div>
                    {social && (
                      <span className="text-[9px] text-gray-300 leading-none">{social.label}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-navy-light pt-6 pb-[160px] md:pb-6">
        <div className="container mx-auto px-4 flex flex-col md:flex-row justify-between items-center md:items-end gap-6">
          
          <div className="flex flex-col items-center md:items-start text-center md:text-left gap-1.5">
            <p className="text-gray-500 text-sm">© 2026 Grand Palace General Trading. All rights reserved.</p>
            <p className="text-gray-500 text-sm">
              Designed and developed by <a href="https://anraone.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#0c1b44] font-medium transition-colors">Anraone</a>
            </p>
          </div>

          <div className="flex flex-col items-center md:items-end gap-3 md:pr-[180px] xl:pr-[200px]">
            <div className="flex items-center gap-4">
              <a href="https://www.facebook.com/profile.php?id=61587535891263" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#0c1b44] transition-colors" title="Facebook">
                <Facebook className="h-5 w-5" />
              </a>
              <a href="https://www.instagram.com/grandpalace.ae?igsh=MWdtaThhcTd6dG5oeA%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#0c1b44] transition-colors" title="Instagram">
                <Instagram className="h-5 w-5" />
              </a>
              <a href="https://www.linkedin.com/in/grand-palace-general-trading-llc-2904063a8?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=ios_app" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#0c1b44] transition-colors" title="LinkedIn">
                <Linkedin className="h-5 w-5" />
              </a>
              <a href="https://www.youtube.com/channel/UCINXOBFtfMPvDSsXRMJM48w" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#0c1b44] transition-colors" title="YouTube">
                <Youtube className="h-5 w-5" />
              </a>
              <a href="https://x.com/GrandPalace2022" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#0c1b44] transition-colors" title="X (Twitter)">
                <Twitter className="h-5 w-5" />
              </a>
              <a href="https://www.tiktok.com/@grandpalace.ae?is_from_webapp=1&sender_device=pc" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#0c1b44] transition-colors" title="TikTok">
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12.525.02c1.31-.02 2.61-.01 3.91.04.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.12-3.44-3.17-3.64-5.46-.24-2.61 1.05-5.23 3.21-6.49 1.63-.94 3.61-1.1 5.43-.65v4.09c-.43-.19-.92-.26-1.38-.2-.84.07-1.65.64-1.99 1.4-.41.92-.23 2.07.45 2.8.63.69 1.65.98 2.56.78 1.13-.25 1.91-1.31 1.91-2.48.01-4.73.01-9.47.01-14.21h-.03z" />
                </svg>
              </a>
            </div>
            <div className="flex flex-col md:flex-row items-center gap-1 md:gap-3 text-gray-500 text-sm text-center md:text-right">
              <p>TRN: {settings.company_trn || settings.vat_trn || '104025022500003'}</p>
              <span className="hidden md:inline text-gray-300">|</span>
              <p>Trade License No. 1091095</p>
            </div>
          </div>

        </div>
      </div>
    </footer>
  );
};

export default Footer;
